import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Slider as ChakraSlider,
  SliderThumb,
  SliderTrack,
  SliderFilledTrack,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FaPlay, FaPause, FaExpand } from "react-icons/fa";
import demoVideo from "../assets/videos/demo.mp4";
import Translate from "../components/LanguageTranslation/Translate";

const VideoSection = () => {
  const history = useHistory();
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false); // Track hover state
  const [isFullscreen, setIsFullscreen] = useState(false); // Track fullscreen state
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(true);

  const videoId = "dQw4w9WgXcQ";
  const embedUrl = `https://www.youtube.com/embed/${videoId}`;

  const handleLinkClick = (message) => {
    localStorage.removeItem("conversation_id"); // Clear any existing conversation
    history.push(
      `/ai-travel-planner/chat?message=${encodeURIComponent(message)}`
    ); // Redirect to the chat system with the clicked text
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullScreenNow = !!(
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );

      setIsFullscreen(isFullScreenNow);

      // When exiting fullscreen, scroll back to video section
      if (!isFullScreenNow) {
        const videoSection = document.getElementById("video-section");
        if (videoSection) {
          videoSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, []);

  return (
    <Box id="video-section" w={{ base: "100%", md: "100%" }}>
      {/* Header Section */}
      <Flex
        direction={{ base: "column-reverse", md: "row" }} // Change direction for smaller screens
        justifyContent="center"
        alignItems="flex-start" // Align contents to the top left
        p={{ base: 1, md: 8, lg: 8, xl: 8 }}
        pt={{ base: 8, md: 8, lg: 20, xl: 20 }}
        pb={{ base: 8, md: 8, lg: 20, xl: 20 }}
        pl={{ base: 4, md: 4, lg: 32, xl: 80 }}
        pr={{ base: 4, md: 4, lg: 32, xl: 80 }}
        bg="white"
        w={{ base: "100%", md: "100%" }}
      >
        {/* Video Section on the left */}
        <Box
          flex="1" // ✅ let it grow like the old video box
          ml={{ base: 0, md: 10 }} // Margin left for spacing
          mb={{ base: 8, md: 0 }}
          /* 🔄  key line: shrink on xl only */
          w={{ base: "100%", md: "50%", lg: "50%", xl: "35%" }} // was xl "50%"
          /* optional – keeps it from ever stretching too wide on super‑large monitors */
          maxW={{  base: "100%", md: "50%", lg: "50%", xl: "720px" }}
          h="100%"
          position="relative"
          borderRadius="30px"
          overflow="hidden"
          boxShadow="md"
          mr={{ base: 0, md: 0 }}
        >
          {/* keep a 16 : 9 rectangle */}
          <Box
            as="iframe"
            width="100%" // leave at 100 %
            height="auto" // let Aspect Ratio rule
            aspectRatio="16 / 9" // Chakra v2≥ only; otherwise use <AspectRatio>
            src={embedUrl}
            borderRadius="lg"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          {/* If you’re on Chakra v2+, you can drop the absolute‐position math and just do:
      <AspectRatio ratio={16 / 9}><iframe … /></AspectRatio> */}
        </Box>

        {/* Text Section on Right with Tabs */}
        <Box
          flex="1"
          ml={{ base: 0, md: 4, lg: 10, xl: 10 }} // Margin left for spacing
          mb={{ base: 8, md: 0 }}
          w={{ base: "100%", md: "50%", lg: "50%", xl: "35%" }}
          maxW={{ xl: "720px" }}
          textAlign="left" // Align text to the left
          justifyContent="flex-start"
        >
          <Heading
            as="h1"
            size={{ base: "30px", md: "md", lg: "lg", xl: "xl" }}
            fontFamily="Poppins, sans-serif"
            textAlign="left" // Align heading to the left
            mb={{ base: 2, md: 2, lg: 2, xl: 4 }}
          >
            <Translate text="Explore the Outdoors" />
          </Heading>

          {/* Tabs Section with custom styling */}
          <Tabs
            variant="unstyled"
            mt={4}
            minW={{ base: "68%", md: "100%", lg: "68%" }}
            maxW={{ base: "90%", md: "100%", lg: "90%" }}
          >
            <TabList>
              <Tab
                _selected={{
                  color: "#286328",
                  fontWeight: "bold",
                  borderBottom: "3px solid #286328",
                }}
                _focus={{ boxShadow: "none" }}
                color="gray.400"
                fontWeight="bold"
                fontSize={{ base: "xs", md: "xs", lg: "sm", xl: "lg" }} // Decreased font size for tabs
                textAlign="left" // Align tab text to the left
                borderBottom="2px solid #e5e5e5"
              >
                <Translate text="ABOUT US" />
              </Tab>
              <Tab
                _selected={{
                  color: "#286328",
                  fontWeight: "bold",
                  borderBottom: "3px solid #286328",
                }}
                _focus={{ boxShadow: "none" }}
                color="gray.400"
                fontWeight="bold"
                fontSize={{ base: "xs", md: "xs", lg: "sm", xl: "lg" }} // Decreased font size for tabs
                textAlign="left" // Align tab text to the left
                borderBottom="2px solid #e5e5e5"
              >
                <Translate text="WHY CHOOSE US" />
              </Tab>
              <Tab
                _selected={{
                  color: "#286328",
                  fontWeight: "bold",
                  borderBottom: "3px solid #286328",
                }}
                _focus={{ boxShadow: "none" }}
                color="gray.400"
                fontWeight="bold"
                fontSize={{ base: "xs", md: "xs", lg: "sm", xl: "lg" }} // Decreased font size for tabs
                textAlign="left" // Align tab text to the left
                borderBottom="2px solid #e5e5e5"
              >
                <Translate text="OUR MISSION" />
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <Text
                  mt={2}
                  fontSize={{ base: "sm", md: "sm", lg: "md", xl: "20px" }}
                  textAlign="left"
                  mb={0}
                >
                  <Text as="span" fontWeight="bold">
                    NOMAD360
                  </Text>{" "}
                  <Translate
                    text="creates personalized travel plans in seconds, tailored to your
                  outdoor lifestyle. Stress-free planning and epic adventures
                  await!"
                  />
                </Text>
              </TabPanel>
              <TabPanel>
                <VStack spacing={{ base: 2, md: 2, lg: 2, xl: 4 }}>
                  <Text
                    mt={2}
                    fontSize={{ base: "sm", md: "sm", lg: "md", xl: "20px" }}
                    textAlign="left"
                    mb={0}
                  >
                    <Text as="span" fontWeight="bold">
                      NOMAD360{" "}
                    </Text>

                    <Translate
                      text="uses the latest AI to create personalized travel plans in
                    seconds. You’ll get helpful tools like a budget planner and
                    packing list to make your trip even easier."
                    />
                  </Text>
                  <Text
                    mb={0}
                    fontSize={{ base: "sm", md: "sm", lg: "md", xl: "20px" }}
                  >
                    <Translate
                      text="We also connect you with other travelers and businesses for
                    real tips and advice. Enjoy smart tech and a great travel
                    community, all in one place."
                    />
                  </Text>
                </VStack>
              </TabPanel>
              <TabPanel>
                <VStack spacing={{ base: 2, md: 2, lg: 2, xl: 4 }}>
                  <Text
                    mt={2}
                    fontSize={{ base: "sm", md: "sm", lg: "md", xl: "20px" }}
                    textAlign="left"
                    mb={0}
                  >
                    <Text as="span" fontWeight="bold">
                      NOMAD360'{" "}
                    </Text>

                    <Translate
                      text="mission is to inspire everyone to connect with nature and
                    explore the amazing places around us."
                    />
                  </Text>
                  <Text
                    mb={0}
                    fontSize={{ base: "sm", md: "sm", lg: "md", xl: "20px" }}
                  >
                    <Translate
                      text="We aim to create unforgettable experiences that not only
                    lead to lasting memories, but also boost your health and
                    mental well-being. By protecting our green planet, we
                    promote a sustainable future for all."
                    />
                  </Text>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <Flex mt={0} ml={4} justifyContent="flex-start">
            <Button
              bg="black"
              color="white"
              // _hover="none"
              as={RouterLink}
              to="/ai-travel-planner"
              size={{ base: "sm", md: "sm", lg: "md", xl: "lg" }}
              mr={4}
              transition="transform 0.3s"
              _hover={{
                transform: "scale(1.1)",
                // border: "2px solid #FFC719",
                // borderColor: "#FFC719",
                bg: "black",
              }}
            >
              <Translate text="Get started" />
            </Button>
            <Button
              variant="outline"
              borderColor="black"
              // _hover="none"
              size={{ base: "sm", md: "sm", lg: "md", xl: "lg" }}
              onClick={() => handleLinkClick("Build an Itinerary")} // Call the function to navigate and scroll
              transition="transform 0.3s"
              _hover={{
                transform: "scale(1.1)",
                // border: "2px solid #FFC719",
                // borderColor: "#FFC719",
                bg: "white",
              }}
            >
              <Translate text="Build an Itinerary" />
            </Button>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

export default VideoSection;
